import { Button, Col, Container, Row, Stack } from "react-bootstrap";
import useMyProperties from "../../../Hooks/UseMyProperties/UseMyPropertiesHook";
import { useAppDispatch } from "../../../redux/store";
import { useState } from "react";
import timeService, { DATE_FORMAT } from "../../../services/time/timeService";
import { BookingEntry } from "../../../Components/Bookings/BookingEntry/BookingEntry";
import { PropertyBookingEntry } from "../../../Types/bookingTypes";
import QuickForm from "../../../Components/Forms/QuickForm/QuickForm";
import useBookingsByPropertyMemo from "../../../Memos/Bookings/UseBookingsByPropertyMemo";
import useListToRefMemo from "../../../Memos/General/UseListToRefMemo";
import { FieldGeneratorFieldType } from "../../../Components/FormFields/FieldGenerator/FieldGenerator";
import { FONTAINE_PUPPET_CHECK_BLACKOUTS, FONTAINE_PUPPET_CHECK_BOOKINGS, FONTAINE_PUPPET_VALIDATE_BOOKING } from "../../../Config/config";
import Collapsible from "../../../Components/Collapsible/Collapsible";
import canAccess from "../../../services/accessService/accessService";
import { showError, showSuccess } from "../../../redux/counter/counterSlice";
import { useBulkEditBookingsMutation, useEditBookingMutation, useGetBookingsQuery, useFontaineCheckoutTodayNoCleaningMutation, useFontaineCleanReadyMutation, useSendBookingGuestEmailMutation } from "../../../services/bClientApi";
import { ConfirmModal } from "../../../Components/Modals/confirmModal";
import { PropertyType } from "../../../Types/propertyTypes";
import { copyToClipboard } from "../../../Helpers/ValueHandlers/copyText";

export default function UnconfirmedBookingsPage() {
    const {
        myProperties,
        publicPropertyIdKeys
    } = useMyProperties()
    const [editBooking] = useEditBookingMutation()
    const [bulkEditBookings] = useBulkEditBookingsMutation()
    const [sendBookingGuestEmail, { isLoading: sendBookingGuestEmailLoading }] = useSendBookingGuestEmailMutation()
    const [triggerCheckoutTodayNoCleaning] = useFontaineCheckoutTodayNoCleaningMutation()
    const [triggerCleanReady] = useFontaineCleanReadyMutation()
    const dispatch = useAppDispatch()

    const [activeConfirmationModal, setActiveConfirmationModal] = useState({
        active: false,
        title: '',
        message: '',
        onConfirm: () => { },
        onClose: () => { }
    })

    const resetConfirmationModal = () => {
        setActiveConfirmationModal({
            active: false,
            title: '',
            message: '',
            onConfirm: () => { },
            onClose: () => { }
        })
    }
    const startOfToday = timeService.getCurrentTime().minus({ days: 1 }).startOf('day').toMillis()
    const queryEndDate = timeService.getCurrentTime().plus({ months: 1 }).endOf('day').toMillis()
    const [activeQuery, setActiveQuery] = useState<any>({
        query: {
            checkIn: startOfToday,
            checkOut: queryEndDate,
            bookingStatus: ['new', 'pending'],
            excludeSource: ['repairs', 'blocked'],
            requiresConfirmation: true,
            confirmationCode: ['', null, 'submitted'],
            propertyId: Object.keys(myProperties),
            includeBookingGuestInfo: true
        },
        limit: 500,
        page: 1
    })

    const queryToUse = {
        bookingsQuery: {
            ...activeQuery.query,
            propertyId: publicPropertyIdKeys
        },
        pagination: {
            page: activeQuery.page,
            limit: activeQuery.limit
        }
    }
    const {
        data,
        // isLoading,
        // isFetching,
        error,
    } = useGetBookingsQuery(queryToUse, { skip: Object.keys(myProperties).length === 0 })

    console.log('UnconfirmedBookingsPage data', data)

    const bookingsList = data?.docs || []

    const [expandAllProperties, setExpandAllProperties] = useState<boolean>(false)
    const [openProperties,] = useState<any>({})

    const [bookingQueue, setBookingQueue] = useState<any>({})
    const [activeConfirmationForm, setActiveConfirmationForm] = useState<string>('')
    const bookingsByProperty = useBookingsByPropertyMemo(bookingsList ? bookingsList : [])
    const bookingRef = useListToRefMemo(bookingsList ? bookingsList : [])
    const [fontaineBlackoutDates, setFontaineBlackoutDates] = useState<any>({})

    const editBookingEntry = async (updatedBooking: PropertyBookingEntry) => {
        if (!updatedBooking._id) { return }
        await editBooking({
            bookingId: updatedBooking._id,
            bookingInfo: updatedBooking
        }).unwrap()
        setActiveConfirmationForm('')
    }

    const createExtraButtons = (booking: PropertyBookingEntry) => {
        const extraButtons: any = []

        if (!canAccess('routes', 'edit-booking', { payload: booking })) {
            return extraButtons
        }

        if (booking.bookingStatus !== 'new') {
            extraButtons.push({
                label: 'Mark New',
                onClick: (b: PropertyBookingEntry) => {
                    editBookingEntry({
                        ...b,
                        bookingStatus: 'new',
                        confirmationCode: ''
                    })
                },
                variant: 'secondary'
            })
        }

        if (booking.confirmationCode !== 'submitted') {
            extraButtons.push({
                label: 'Mark Submitted',
                onClick: (b: PropertyBookingEntry) => {
                    editBookingEntry({
                        ...b,
                        bookingStatus: 'pending',
                        confirmationCode: 'submitted'
                    })
                },
                variant: 'warning'
            })
        }

        if (booking._id) {
            if (!bookingQueue[booking._id]) {
                extraButtons.push({
                    label: 'Add to Queue',
                    onClick: addBookingToQueue,
                    variant: 'primary'
                })
            }
            if (bookingQueue[booking._id]) {
                extraButtons.push({
                    label: 'Remove from Queue',
                    onClick: removeBookingFromQueue,
                    variant: 'danger'
                })
            }
        }

        if ((!booking.confirmationCode || booking.confirmationCode === 'submitted') && activeConfirmationForm !== booking._id) {
            extraButtons.push({
                label: 'Add Confirmation',
                onClick: (b: PropertyBookingEntry) => {
                    setActiveConfirmationForm(b._id ? b._id : '')
                },
                variant: 'success'
            })
        }

        // Send/Resend booking guest email button
        if (booking.bookingGuestInfo && booking.bookingGuestInfo._id && canAccess('bookingGuest', 'sendConfirm')) {
            const bookingGuestId = booking.bookingGuestInfo._id
            const bookingGuestStatus = booking.bookingGuestInfo.status
            extraButtons.push({
                label: `${bookingGuestStatus !== 'new' ? 'Resend ' : 'Send '}✉️`,
                disabled: sendBookingGuestEmailLoading,
                variant: 'primary',
                onClick: () => {
                    setActiveConfirmationModal({
                        active: true,
                        title: `${bookingGuestStatus !== 'new' ? 'Resend' : 'Send'} Confirmation Email`,
                        message: `Are you sure you want to ${bookingGuestStatus !== 'new' ? 'resend' : 'send'} the confirmation email?`,
                        onConfirm: async () => {
                            const mailInfo = await sendBookingGuestEmail(
                                bookingGuestId
                            ).unwrap()
                            console.log('mailInfo', mailInfo)
                            dispatch(showSuccess('Email sent'))
                        },
                        onClose: resetConfirmationModal
                    })
                }
            })
        }

        return extraButtons
    }

    const markAllAsSubmitted = async () => {
        if (!bookingQueue) { return }
        const bookingsToEdit = Object.keys(bookingQueue).map(b => {
            return {
                ...bookingRef[b],
                bookingStatus: 'pending',
                confirmationCode: 'submitted'
            }
        })
        setBookingQueue({})
        await bulkEditBookings(bookingsToEdit).unwrap()
    }

    const addBookingToQueue = (b: PropertyBookingEntry) => {
        if (b._id) {
            const newQueue = { ...bookingQueue }
            newQueue[b._id] = b
            setBookingQueue({ ...newQueue })
        }
    }

    const queueAllBookings = () => {
        if (bookingsList) {
            const newQueue: any = {}
            bookingsList.forEach((b: PropertyBookingEntry) => {
                if (b._id) {
                    newQueue[b._id] = b
                }
            })
            setBookingQueue({ ...newQueue })
        }
    }

    const removeBookingFromQueue = (b: PropertyBookingEntry) => {
        if (b._id) {
            const newQueue = { ...bookingQueue }
            delete newQueue[b._id]
            setBookingQueue({ ...newQueue })
        }
    }

    const handleUnconfirmedBookingsFormSubmit = async (data: any) => {
        const newQuery = {
            ...activeQuery,
            query: {
                ...activeQuery.query,
                confirmationCode: ['', null, 'submitted']
            }
        }


        setBookingQueue({})
        setActiveConfirmationForm('')

        if (data.checkIn) {
            newQuery.query.checkIn = timeService.dateToMillis(data.checkIn)
        } else {
            newQuery.query.checkIn = null
        }
        if (data.checkOut) {
            newQuery.query.checkOut = timeService.dateToMillis(data.checkOut)
            // newBlockQuery.query.checkOut = timeService.dateToMillis(data.checkOut)
        } else {
            newQuery.query.checkOut = null
        }
        if (data.hideSubmitted === false) {
            newQuery.query.confirmationCode.push('submitted')
        }
        setActiveQuery(newQuery)
    }

    const forceFontaineValidation = async () => {
        const response = await fetch(FONTAINE_PUPPET_VALIDATE_BOOKING, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            },
            // body: fontainePayload
        }).then((res) => {
            return res.json()
        }).catch((err) => {
            return {
                error: err
            }
        })

        console.log(response)
        if (response.error) {
            dispatch(showError(response.error))
        } else {
            dispatch(showSuccess('Request has been sent for puppet to validate and email with results will be sent shortly'))
        }
    }

    const forceFontainePuppet = async () => {
        const fontaineResponse = await fetch(FONTAINE_PUPPET_CHECK_BOOKINGS, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            },
            // body: fontainePayload
        }).then((res) => {
            return res.json()
        }).catch((err) => {
            return {
                error: err
            }
        })

        console.log(fontaineResponse)
        if (fontaineResponse.error) {
            dispatch(showError(fontaineResponse.error))
        } else {
            dispatch(showSuccess('Request has been sent to puppet and email with results will be sent shortly'))
        }
    }

    const checkFontaineBlackoutDates = async () => {
        setFontaineBlackoutDates({
            loading: true
        })
        const fontaineResponse = await fetch(FONTAINE_PUPPET_CHECK_BLACKOUTS, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            },
            // body: fontainePayload
        }).then((res) => {
            return res.json()
        }).catch((err) => {
            return {
                error: err
            }
        })
        console.log(fontaineResponse)
        setFontaineBlackoutDates({
            loading: false,
            ...fontaineResponse,
            blackoutLines: fontaineResponse.blackoutDates.blackoutLines
        })
    }

    const sendCheckoutTodayNoCleaning = async () => {
        try {
            const response = await triggerCheckoutTodayNoCleaning().unwrap()
            dispatch(showSuccess(response.message || 'Checkout today email sent'))
        } catch (err: any) {
            dispatch(showError(err?.data?.error || 'Failed to send checkout today email'))
        }
    }

    const sendCleanReady = async () => {
        try {
            const response = await triggerCleanReady().unwrap()
            dispatch(showSuccess(response.message || 'Clean ready email sent'))
        } catch (err: any) {
            dispatch(showError(err?.data?.error || 'Failed to send clean ready email'))
        }
    }

    const copyReservations = () => {
        const copyData = createReservationsLogic(myProperties, bookingQueue)
        copyToClipboard(copyData)
    }

    return (
        <Container>
            <Row>
                <Col>
                    <h1>Unconfirmed Bookings</h1>
                    <hr />
                    {error && (<>{JSON.stringify(error)}<br /></>)}

                    {canAccess('routes', 'fontaine-tools') && (
                        <>
                            {/* force puppet button */}
                            <Button onClick={forceFontainePuppet} className="me-2 mb-2">
                                Force Fontaine
                            </Button>

                            {/* check blackout dates */}
                            <Button onClick={checkFontaineBlackoutDates} className="me-2 mb-2">
                                Check Blackout Dates
                            </Button>

                            {/* validate fontaine booking */}
                            <Button onClick={forceFontaineValidation} className="me-2 mb-2">
                                Validate Fontaine Books
                            </Button>

                            {/* send checkout today no cleaning email */}
                            <Button onClick={() => {
                                setActiveConfirmationModal({
                                    active: true,
                                    title: 'Send CheckOuts Today',
                                    message: 'Are you sure you want to send the checkout today email to Fontainebleau? This will notify them of guests checking out today and to not charge a cleaning fee.',
                                    onConfirm: () => sendCheckoutTodayNoCleaning(),
                                    onClose: resetConfirmationModal
                                })
                            }} className="me-2 mb-2" variant="warning">
                                Send CheckOuts Today
                            </Button>

                            {/* send checkin today email */}
                            {/* <Button onClick={() => {
                                setActiveConfirmationModal({
                                    active: true,
                                    title: 'Send CheckIns Today',
                                    message: 'Are you sure you want to send the checkin today email to Fontainebleau? This will notify them of guests checking in today.',
                                    onConfirm: () => sendCheckinToday(),
                                    onClose: resetConfirmationModal
                                })
                            }} className="me-2 mb-2" variant="info">
                                Send CheckIns Today
                            </Button> */}

                            {/* send clean ready email */}
                            <Button onClick={() => {
                                setActiveConfirmationModal({
                                    active: true,
                                    title: 'Send Clean Ready',
                                    message: 'Are you sure you want to send the clean ready email to Fontainebleau? This will notify them of properties that are clean and ready for check-ins.',
                                    onConfirm: () => sendCleanReady(),
                                    onClose: resetConfirmationModal
                                })
                            }} className="me-2 mb-2" variant="success">
                                Send Clean Ready
                            </Button>

                            <div className="w-full my-2 small fst-italic">
                                NOTE: Puppet runs every 15 minutes
                            </div>

                            <hr />
                            {fontaineBlackoutDates && Object.keys(fontaineBlackoutDates).length > 0 && (
                                <div className="mb-4">
                                    <h3>Blackout Dates</h3>
                                    {fontaineBlackoutDates.loading && (
                                        <div>Loading...</div>
                                    )}
                                    {fontaineBlackoutDates.error && (
                                        <div>{fontaineBlackoutDates.error}</div>
                                    )}

                                    {fontaineBlackoutDates.blackoutLines && fontaineBlackoutDates.blackoutLines.map((x: any, idx: number) => (
                                        <div key={idx}>
                                            {x}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    <QuickForm
                        initialData={{
                            checkIn: startOfToday,
                            checkOut: queryEndDate,
                            hideSubmitted: false,
                        }}
                        onSubmit={handleUnconfirmedBookingsFormSubmit}
                        fields={unconfirmedBookingsFormFields}
                    />
                    <hr />

                    <Button
                        onClick={() => {
                            setExpandAllProperties(!expandAllProperties)
                        }}
                        className="me-2 mb-2"
                        variant={'secondary'}>
                        {expandAllProperties || Object.keys(openProperties).length > 0 ? 'Collapse All' : 'Expand All'}
                    </Button>

                    {bookingQueue && bookingsList?.length && Object.keys(bookingQueue).length < bookingsList?.length ? (
                        <Button
                            onClick={queueAllBookings}
                            className="me-2 mb-2"
                            variant={'primary'}>
                            Queue All
                        </Button>
                    ) : (
                        <Button
                            onClick={() => {
                                setBookingQueue({})
                            }}
                            className="me-2 mb-2"
                            variant={'danger'}>
                            Clear Queue
                        </Button>
                    )}


                    {data?.totalDocs && (
                        <div>
                            {data.totalDocs} unconfirmed
                        </div>
                    )}
                    <hr />



                    {bookingsByProperty.map((property: { propertyId: string, bookings: PropertyBookingEntry[] }, idx: number) => (
                        <div key={`${property.propertyId}-${idx}`}>
                            <Collapsible
                                key={expandAllProperties ? `expanded-${idx}` : `closed-${idx}`}
                                initialValue={expandAllProperties}
                                title={myProperties[property.propertyId]?.title}
                                TitleTag={'h3'}
                            >
                                <div className="booking-entry-collapse-wrapper">

                                    {property.bookings.map((booking: PropertyBookingEntry) => (
                                        <BookingEntry
                                            key={booking._id}
                                            bookingInfo={booking}
                                            propertyName={myProperties[property.propertyId]?.title}
                                            extraButtons={createExtraButtons(booking)}
                                        >
                                            {activeConfirmationForm === booking._id && (
                                                <QuickForm
                                                    initialData={{
                                                        confirmationCode: booking.confirmationCode
                                                    }}
                                                    onSubmit={async (data) => {
                                                        editBookingEntry({
                                                            ...booking,
                                                            confirmationCode: data.confirmationCode,
                                                            bookingStatus: 'confirmed'
                                                        })
                                                    }}
                                                    onCancel={() => { setActiveConfirmationForm('') }}
                                                    fields={[
                                                        {
                                                            fieldName: 'confirmationCode',
                                                            fieldLabel: 'Confirmation Code',
                                                            fieldType: 'text',
                                                            required: true
                                                        }
                                                    ]}
                                                />
                                            )}
                                        </BookingEntry>
                                    ))}
                                </div>

                            </Collapsible>
                            <hr />
                            {/* </Collapse> */}
                        </div>
                    ))}
                </Col>
            </Row>

            {/* Queue and copy data Block */}
            {Object.keys(bookingQueue).length > 0 && (
                <Row>
                    <Col>
                        <h2>Queue</h2>
                        <hr />
                        <Stack gap={3} className={'mb-3'}>
                            {Object.keys(bookingQueue).map((bookingId, idx) => {
                                const booking: PropertyBookingEntry = bookingRef[bookingId] ? bookingRef[bookingId] : bookingQueue[bookingId]
                                return (
                                    <div key={`${bookingId}-${idx}`}>
                                        <div className="booking-queue-info mb-2">
                                            {myProperties &&
                                                myProperties[booking.propertyId] && (
                                                    <b>
                                                        {myProperties[booking.propertyId].title}
                                                    </b>
                                                )} <br />
                                            {booking.bookingStatus} <br />
                                            {booking?.contactInfo?.firstName} {booking?.contactInfo?.lastName} <br />
                                            {timeService.getFormattedTime(booking.checkIn)} -&nbsp;
                                            {timeService.getFormattedTime(booking.checkOut)}
                                        </div>
                                        <Button
                                            onClick={() => { removeBookingFromQueue(booking) }}
                                            size={'sm'}
                                            variant="secondary">
                                            Remove
                                        </Button>
                                    </div>
                                )
                            })}
                        </Stack>
                        <hr />

                        <pre>
                            {/* LOGIC GOES HERE */}
                            {createReservationsLogic(myProperties, bookingQueue)}
                        </pre>

                        <hr />
                        <Button onClick={copyReservations} className="me-2 mb-2">
                            Copy Reservations
                        </Button>

                        <Button onClick={markAllAsSubmitted} className="me-2 mb-2">
                            Mark All Submitted
                        </Button>

                        <hr />
                    </Col>
                </Row>
            )}

            <ConfirmModal
                active={activeConfirmationModal.active}
                title={activeConfirmationModal.title}
                message={activeConfirmationModal.message}
                onConfirm={() => {
                    activeConfirmationModal.onConfirm()
                    resetConfirmationModal()
                }}
                onClose={() => {
                    activeConfirmationModal.onClose()
                }}
            />
        </Container>
    )
}

// Helper stuff
const unconfirmedBookingsFormFields: FieldGeneratorFieldType[] = [
    {
        fieldName: 'checkIn',
        fieldLabel: 'Check In',
        fieldType: 'date',
        required: true
    },
    {
        fieldName: 'checkOut',
        fieldLabel: 'Check Out',
        fieldType: 'date',
        // required: true
    },
    {
        fieldName: 'hideSubmitted',
        fieldLabel: 'Hide Submitted',
        fieldType: 'checkbox',
        required: false
    }
]

// const monthsKey = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']



export const createReservationsLogic = (
    myProperties: { [propertyId: string]: PropertyType; },
    bookingQueue: { [key: string]: PropertyBookingEntry },
    options: { disableNewBookings?: boolean; } = {}
) => {

    const {
        disableNewBookings = false
    } = options

    let copyText = ''
    const selectedBookingsByProperty: any = {}
    Object.keys(bookingQueue).forEach((bookingId) => {
        const booking = bookingQueue[bookingId]
        if (!selectedBookingsByProperty[booking.propertyId]) {
            selectedBookingsByProperty[booking.propertyId] = {
                bookings: [],
                propertyData: myProperties[booking.propertyId]
            }
        }
        selectedBookingsByProperty[booking.propertyId].bookings.push(booking)
    })

    // loop through properties and add to copyText
    Object.keys(selectedBookingsByProperty).forEach((propertyId: string) => {
        const {
            propertyData, bookings
        } = selectedBookingsByProperty[propertyId] || {}
        if (!propertyData) { return }
        if (!bookings || bookings.length === 0) { return }

        const unitNumber = propertyData?.address?.address2 || 'Unknown Property'

        bookings.sort((x: PropertyBookingEntry, y: PropertyBookingEntry) => {
            return timeService.dateToMillis(x.checkIn) - timeService.dateToMillis(y.checkIn)
        }).forEach((x: PropertyBookingEntry) => {
            const [, checkInMonth, checkInDay] = timeService.getFormattedTime(x.checkIn, DATE_FORMAT).split('/')
            const [, checkOutMonth, checkOutDay] = timeService.getFormattedTime(x.checkOut, DATE_FORMAT).split('/')
            const resString = `${unitNumber} - ${x.contactInfo?.firstName || 'Unknown Guest'} ${x.contactInfo?.lastName || ''} - ${disableNewBookings ? 'Booking' : 'New booking'} - ${checkInMonth}/${checkInDay} - ${checkOutMonth}/${checkOutDay}`
            copyText += `${resString}\n`
        })
        copyText += '\n'
        copyText += '-------------------------------------------------------------\n\n'

    })

    return copyText
}